import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { translations } from "../i18n/translations";

const LanguageContext = createContext(null);

const setMeta = (selector, attr, value) => {
  if (typeof document === "undefined") return;
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("es");

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === "es" ? "en" : "es"));
  }, []);

  // Sync SEO meta tags + html lang attribute with selected language
  useEffect(() => {
    const t = translations[lang];
    if (typeof document === "undefined" || !t?.seo) return;

    document.documentElement.lang = t.seo.htmlLang;
    document.title = t.seo.title;

    setMeta('meta[name="description"]', "content", t.seo.description);
    setMeta('meta[property="og:title"]', "content", t.seo.title);
    setMeta('meta[property="og:description"]', "content", t.seo.description);
    setMeta('meta[property="og:locale"]', "content", t.seo.ogLocale);
    setMeta('meta[name="twitter:title"]', "content", t.seo.title);
    setMeta('meta[name="twitter:description"]', "content", t.seo.description);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang,
      t: translations[lang],
    }),
    [lang, toggleLang]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
};
